import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RootState from "../../../redux/states/root.state";
import { AppDispatch } from "../../../redux/store";
import { markNotificationReadApi } from "../../../services/api/notifications.api";
import { decrementUnreadNotifications } from "../../../redux/reducers/user.reducer";
import NewsReader from "./NewsReader";
import { BellIcon, ClockIcon } from "../../../components/common/Icons/Icons";

const NotificationsList: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { data: notifications, loading, meta } = useSelector((state: RootState) => state.notifications);
  const user = useSelector((state: RootState) => state.user.data);
  const accessToken = useSelector((state: RootState) => state.user.accessToken);
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localReadIds, setLocalReadIds] = useState<number[]>([]);

  // Mark first notification as read on load
  useEffect(() => {
    if (notifications.length > 0 && accessToken) {
      const first = notifications[0];
      if (!first.read_by?.includes(user?.id) && !localReadIds.includes(first.id)) {
        setLocalReadIds(prev => [...prev, first.id]);
        dispatch(decrementUnreadNotifications());
        markNotificationReadApi(accessToken, first.id).catch(() => {});
      }
    }
  }, [notifications, accessToken, user?.id]);

  const isRead = (notif: any) => {
    if (localReadIds.includes(notif.id)) return true;
    return notif.read_by?.includes(user?.id);
  };

  const handleTabChange = async (_e: any, val: number) => {
    setActiveTab(val);
    const notif = notifications[val];
    if (notif && !isRead(notif)) {
      setLocalReadIds(prev => [...prev, notif.id]);
      dispatch(decrementUnreadNotifications());
      try {
        await markNotificationReadApi(accessToken, notif.id);
      } catch (_) {}
    }
  };

  const handlePageChange = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
    setActiveTab(0);
    setLocalReadIds([]);
  };

  // List: the time for today's, "Yesterday", otherwise the date
  const listAside = (notif: any) => {
    if (!notif.sent_at) return null;
    const sent = new Date(notif.sent_at);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (sent.toDateString() === today.toDateString()) return sent.toLocaleTimeString(undefined, {hour: 'numeric', minute: '2-digit'});
    if (sent.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return sent.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
  };

  const sentFull = (notif: any) => new Date(notif.sent_at).toLocaleString(undefined, {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

  return (
    <NewsReader
      title="Notifications"
      total={meta?.total ?? notifications.length}
      items={notifications}
      loading={loading}
      emptyText="No notifications at this time."
      activeIndex={activeTab}
      onSelect={(i) => handleTabChange(null, i)}
      page={searchParams.get("page") ? Number(searchParams.get("page")) : 1}
      totalPages={meta?.total_pages ?? 1}
      onPage={handlePageChange}
      icon={<BellIcon/>}
      bodyOf={(notif) => notif.text}
      listAside={listAside}
      detailMeta={(notif) => notif.sent_at && <span className="nw-meta-text"><ClockIcon/> Sent {sentFull(notif)}</span>}
      ctaLabel={() => 'View'}
      kind="Notification"
      isUnread={(notif) => !isRead(notif)}
    />
  );
};

export default NotificationsList;
